import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { Briefcase, BookmarkCheck, ChevronDown, ChevronUp, ExternalLink, Handshake, HeartHandshake, LoaderCircle, Mail, MapPin, Phone, Search, Send, Star, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { EASE } from "@/components/landing/Reveal";
import { PARTNER_CATEGORIES, RESOURCE_TYPES } from "@/lib/partners";

function Stars({ value, onChange, size = "h-3.5 w-3.5" }) {
  return (
    <span className="flex gap-0.5">
      {[1, 2, 3, 4, 5].map((n) => (
        <button key={n} type="button" disabled={!onChange} onClick={() => onChange?.(n)}
          className={onChange ? "cursor-pointer" : "cursor-default"} aria-label={`${n} étoile${n > 1 ? "s" : ""}`}
          data-testid={onChange ? `review-star-${n}` : undefined}>
          <Star className={`${size} ${n <= Math.round(value || 0) ? "fill-ambre text-ambre" : "text-slate-200"}`} />
        </button>
      ))}
    </span>
  );
}

function ReviewSection({ p }) {
  const [rating, setRating] = useState(p.my_review?.rating || 0);
  const [comment, setComment] = useState(p.my_review?.comment || "");
  const [sending, setSending] = useState(false);
  const [summary, setSummary] = useState({ avg: p.rating_avg, count: p.rating_count });
  const [reviews, setReviews] = useState(p.reviews || []);

  const submit = async () => {
    if (!rating || sending) return;
    setSending(true);
    try {
      const { data } = await api.post(`/partners/${p.id}/reviews`, { rating, comment: comment.trim() || null });
      setSummary({ avg: data.rating_avg, count: data.rating_count });
      setReviews((r) => [{ rating, comment: comment.trim() || null, author: "Vous", created_at: new Date().toISOString() },
        ...r.filter((x) => x.author !== "Vous")].slice(0, 5));
      toast.success("Merci ! Votre avis aide les autres utilisateurs.");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Impossible d'enregistrer votre avis.");
    } finally { setSending(false); }
  };

  return (
    <div className="mt-3 rounded-xl border border-slate-100 p-4" data-testid="partner-reviews-section">
      <div className="flex flex-wrap items-center gap-2">
        <Stars value={summary.avg} />
        <p className="text-xs font-bold text-slate-700" data-testid="partner-rating-summary">
          {summary.count > 0 ? `${summary.avg}/5 · ${summary.count} avis` : "Aucun avis pour le moment — soyez le premier !"}
        </p>
      </div>
      {reviews.length > 0 && (
        <div className="mt-3 space-y-2">
          {reviews.map((r, i) => (
            <div key={i} className="rounded-lg bg-slate-50 p-3" data-testid="partner-review-item">
              <div className="flex items-center gap-2">
                <Stars value={r.rating} size="h-3 w-3" />
                <p className="text-[11px] font-bold text-slate-600">{r.author}</p>
                <p className="text-[10px] text-slate-400">{new Date(r.created_at).toLocaleDateString("fr-FR")}</p>
              </div>
              {r.comment && <p className="mt-1 text-xs leading-relaxed text-slate-600">{r.comment}</p>}
            </div>
          ))}
        </div>
      )}
      <div className="mt-3 border-t border-slate-100 pt-3">
        <p className="text-[11px] font-bold uppercase tracking-wider text-slate-400">{p.my_review ? "Modifier mon avis" : "Donner mon avis"}</p>
        <div className="mt-2 flex flex-wrap items-center gap-3">
          <Stars value={rating} onChange={setRating} size="h-5 w-5" />
          <input value={comment} onChange={(e) => setComment(e.target.value)} maxLength={600}
            placeholder="Votre commentaire (optionnel)"
            className="min-w-[180px] flex-1 rounded-xl border border-slate-200 px-3.5 py-2 text-xs focus:border-bleu focus:outline-none"
            data-testid="review-comment-input" />
          <button onClick={submit} disabled={!rating || sending}
            className="btn-primary !px-4 !py-2 !text-[11px] disabled:opacity-50" data-testid="review-submit-button">
            {sending ? <LoaderCircle className="h-3 w-3 animate-spin" /> : <><Send className="h-3 w-3" /> Publier</>}
          </button>
        </div>
      </div>
    </div>
  );
}

function PartnerCard({ p, index, appliedJobIds, onTrack }) {
  const [open, setOpen] = useState(false);
  return (
    <motion.div initial={{ opacity: 0, y: 14 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.4, delay: index * 0.05, ease: EASE }}
      className="card-integra p-5" data-testid="partner-card">
      <div className="flex flex-wrap items-center gap-2">
        <span className="rounded-full bg-bleu-light px-2.5 py-1 text-[10px] font-bold text-bleu">{p.category_label}</span>
        {p.is_nonprofit && (
          <span className="flex items-center gap-1 rounded-full bg-emerald-100 px-2.5 py-1 text-[10px] font-bold text-emerald-700">
            <HeartHandshake className="h-3 w-3" /> Bénévolat
          </span>
        )}
        {p.city && <span className="flex items-center gap-1 text-[11px] text-slate-400"><MapPin className="h-3 w-3" /> {p.city}</span>}
      </div>
      <p className="mt-2.5 text-base font-extrabold text-slate-900" data-testid="partner-card-org">{p.organization}</p>
      {p.rating_count > 0 && (
        <div className="mt-1 flex items-center gap-1.5" data-testid="partner-card-rating">
          <Stars value={p.rating_avg} /><span className="text-[11px] font-bold text-slate-500">{p.rating_avg}/5 ({p.rating_count})</span>
        </div>
      )}
      <p className="mt-1 text-xs leading-relaxed text-slate-600">{p.description}</p>

      <div className="mt-3 flex flex-wrap items-center gap-2 text-[11px]">
        {p.website && (
          <a href={p.website} target="_blank" rel="noreferrer"
            className="flex items-center gap-1.5 rounded-full bg-bleu px-3.5 py-1.5 font-bold text-white transition-colors hover:bg-bleu/90"
            data-testid="partner-card-link">
            Lien partenaire <ExternalLink className="h-3 w-3" />
          </a>
        )}
        <a href={`mailto:${p.email}`} className="flex items-center gap-1.5 rounded-full border border-slate-200 px-3.5 py-1.5 font-semibold text-slate-600 hover:border-bleu/40 hover:text-bleu" data-testid="partner-card-email">
          <Mail className="h-3 w-3" /> {p.email}
        </a>
        {p.phone && (
          <a href={`tel:${p.phone}`} className="flex items-center gap-1.5 rounded-full border border-slate-200 px-3.5 py-1.5 font-semibold text-slate-600 hover:border-bleu/40 hover:text-bleu" data-testid="partner-card-phone">
            <Phone className="h-3 w-3" /> {p.phone}
          </a>
        )}
      </div>

      {(p.jobs || []).length > 0 && (
        <div className="mt-3 space-y-2" data-testid="partner-card-jobs">
          <p className="flex items-center gap-1.5 text-xs font-bold text-slate-700"><Briefcase className="h-3.5 w-3.5 text-bleu" /> Offres d'emploi</p>
          {p.jobs.map((j) => (
            <div key={j.id} className="rounded-xl border border-bleu/15 bg-bleu-light/40 p-3.5" data-testid="partner-job-card">
              <div className="flex flex-wrap items-center gap-2">
                <span className="rounded-full bg-bleu px-2 py-0.5 text-[10px] font-bold text-white">{j.contract_type}</span>
                <p className="text-xs font-bold text-slate-900">{j.title}</p>
                <span className="flex items-center gap-1 text-[10px] text-slate-500"><MapPin className="h-3 w-3" /> {j.location}</span>
              </div>
              <p className="mt-1 whitespace-pre-wrap text-xs leading-relaxed text-slate-600">{j.description}</p>
              <div className="mt-2 flex flex-wrap gap-2">
                {j.apply_email && (
                  <a href={`mailto:${j.apply_email}?subject=Candidature — ${encodeURIComponent(j.title)}`}
                    className="rounded-full bg-bleu px-3.5 py-1.5 text-[10px] font-bold text-white hover:bg-bleu/90" data-testid="job-apply-email">
                    Candidater par email
                  </a>
                )}
                {j.apply_url && (
                  <a href={j.apply_url} target="_blank" rel="noreferrer"
                    className="flex items-center gap-1 rounded-full border border-bleu/30 px-3.5 py-1.5 text-[10px] font-bold text-bleu hover:bg-white" data-testid="job-apply-link">
                    Postuler en ligne <ExternalLink className="h-2.5 w-2.5" />
                  </a>
                )}
                {appliedJobIds.has(j.id) ? (
                  <span className="flex items-center gap-1 rounded-full bg-emerald-100 px-3.5 py-1.5 text-[10px] font-bold text-emerald-700" data-testid="job-tracked-badge">
                    <BookmarkCheck className="h-3 w-3" /> Candidature suivie
                  </span>
                ) : (
                  <button onClick={() => onTrack(j.id)}
                    className="flex items-center gap-1 rounded-full border border-emerald-300 px-3.5 py-1.5 text-[10px] font-bold text-emerald-700 hover:bg-emerald-50"
                    data-testid="job-track-button">
                    <BookmarkCheck className="h-3 w-3" /> J'ai candidaté
                  </button>
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      {p.resources.length > 0 && (
        <>
          <button onClick={() => setOpen(!open)} className="mt-3 flex items-center gap-1.5 text-xs font-bold text-bleu hover:underline" data-testid="partner-card-resources-toggle">
            {open ? <ChevronUp className="h-3.5 w-3.5" /> : <ChevronDown className="h-3.5 w-3.5" />}
            {p.resources.length} ressource{p.resources.length > 1 ? "s" : ""} (contacts, tarifs, bénévolat…)
          </button>
          {open && (
            <div className="mt-2 space-y-2" data-testid="partner-card-resources">
              {p.resources.map((r) => (
                <div key={r.id} className="rounded-xl bg-slate-50 p-3.5">
                  <div className="flex flex-wrap items-center gap-2">
                    <span className="rounded-full bg-white px-2 py-0.5 text-[10px] font-bold text-bleu shadow-sm">
                      {RESOURCE_TYPES.find((t) => t.value === r.type)?.label || r.type}
                    </span>
                    <p className="text-xs font-bold text-slate-900">{r.title}</p>
                  </div>
                  <p className="mt-1 whitespace-pre-wrap text-xs leading-relaxed text-slate-600">{r.content}</p>
                  {r.url && <a href={r.url} target="_blank" rel="noreferrer" className="mt-1 inline-block text-[11px] font-semibold text-bleu hover:underline">{r.url}</a>}
                </div>
              ))}
            </div>
          )}
        </>
      )}

      <ReviewSection p={p} />
    </motion.div>
  );
}

const APP_STATUSES = [
  { value: "envoyee", label: "Envoyée", cls: "bg-bleu-light text-bleu" },
  { value: "entretien", label: "Entretien", cls: "bg-amber-100 text-amber-700" },
  { value: "acceptee", label: "Acceptée", cls: "bg-emerald-100 text-emerald-700" },
  { value: "refusee", label: "Refusée", cls: "bg-rose-100 text-rose-600" },
];

function MesCandidatures({ apps, onUpdate, onRemove }) {
  const [open, setOpen] = useState(true);
  if (apps.length === 0) return null;
  return (
    <div className="card-integra p-5" data-testid="my-applications-section">
      <button onClick={() => setOpen(!open)} className="flex w-full items-center gap-2 text-left" data-testid="my-applications-toggle">
        <BookmarkCheck className="h-4 w-4 text-bleu" />
        <p className="flex-1 text-sm font-bold text-slate-900">Mes candidatures ({apps.length})</p>
        {open ? <ChevronUp className="h-4 w-4 text-slate-400" /> : <ChevronDown className="h-4 w-4 text-slate-400" />}
      </button>
      {open && (
        <div className="mt-3 space-y-2">
          {apps.map((a) => (
            <div key={a.id} className="flex flex-wrap items-center gap-3 rounded-xl border border-slate-100 p-3.5" data-testid="my-application-item">
              <div className="min-w-0 flex-1">
                <p className="text-xs font-bold text-slate-900">{a.job.title}</p>
                <p className="text-[11px] text-slate-400">
                  {a.organization ? `${a.organization} · ` : ""}{a.job.contract_type} · {a.job.location} · suivi depuis le {new Date(a.created_at).toLocaleDateString("fr-FR")}
                </p>
              </div>
              <select value={a.status} onChange={(e) => onUpdate(a.id, e.target.value)}
                className={`rounded-lg border-0 px-2.5 py-1.5 text-[11px] font-bold focus:outline-none ${APP_STATUSES.find((s) => s.value === a.status)?.cls || ""}`}
                data-testid="application-status-select">
                {APP_STATUSES.map((s) => <option key={s.value} value={s.value}>{s.label}</option>)}
              </select>
              <button onClick={() => onRemove(a.id)} aria-label="Retirer du suivi" className="text-slate-300 transition-colors hover:text-rouge" data-testid="application-remove-button">
                <Trash2 className="h-4 w-4" />
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export default function Partenaires() {
  const [data, setData] = useState(null);
  const [filter, setFilter] = useState("");
  const [query, setQuery] = useState("");
  const [apps, setApps] = useState([]);

  useEffect(() => {
    api.get("/partners").then((r) => setData(r.data)).catch(() => toast.error("Impossible de charger les partenaires."));
    api.get("/my-applications").then((r) => setApps(r.data)).catch(() => {});
  }, []);

  const appliedJobIds = new Set(apps.map((a) => a.job_id));

  const track = async (jobId) => {
    try {
      const { data: a } = await api.post(`/jobs/${jobId}/apply`);
      setApps((list) => [a, ...list.filter((x) => x.id !== a.id)]);
      toast.success("Candidature ajoutée à votre suivi.");
    } catch { toast.error("Impossible de suivre cette candidature."); }
  };

  const updateApp = async (id, status) => {
    try {
      await api.patch(`/my-applications/${id}`, { status });
      setApps((list) => list.map((a) => (a.id === id ? { ...a, status } : a)));
      toast.success("Statut de candidature mis à jour.");
    } catch { toast.error("Impossible de mettre à jour le statut."); }
  };

  const removeApp = async (id) => {
    try {
      await api.delete(`/my-applications/${id}`);
      setApps((list) => list.filter((a) => a.id !== id));
      toast.success("Candidature retirée du suivi.");
    } catch { toast.error("Impossible de retirer la candidature."); }
  };

  const q = query.trim().toLowerCase();
  const matches = (p) => !q || [
    p.organization, p.description, p.city, p.category_label,
    ...p.resources.map((r) => `${r.title} ${r.content}`),
    ...(p.jobs || []).map((j) => `${j.title} ${j.location} ${j.contract_type}`),
  ].filter(Boolean).some((s) => s.toLowerCase().includes(q));

  const partners = (data?.partners || []).filter((p) => (!filter || p.category === filter) && matches(p));

  return (
    <div className="mx-auto max-w-3xl space-y-6" data-testid="page-partenaires">
      <div>
        <h1 className="flex items-center gap-2.5 font-display text-2xl font-extrabold text-slate-900 sm:text-3xl">
          <Handshake className="h-7 w-7 text-bleu" /> Partenaires
        </h1>
        <p className="mt-1 text-sm text-slate-500">
          Avocats, associations, centres de formation et recruteurs partenaires d'INTEGRA FRANCE :
          leurs contacts, tarifs et actions bénévoles pour vous accompagner.
        </p>
      </div>

      <MesCandidatures apps={apps} onUpdate={updateApp} onRemove={removeApp} />

      <div className="relative" data-testid="partners-search">
        <Search className="pointer-events-none absolute left-4 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
        <input
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder="Rechercher par ville, nom, service, métier… (ex : Paris, avocat, CDI, français)"
          className="w-full rounded-2xl border border-slate-200 bg-white py-3 pl-11 pr-4 text-sm placeholder:text-slate-400 focus:border-bleu focus:outline-none focus:ring-2 focus:ring-bleu/15"
          data-testid="partners-search-input"
        />
      </div>

      <div className="flex flex-wrap gap-2" data-testid="partners-filters">
        <button onClick={() => setFilter("")} className={`rounded-full px-4 py-2 text-xs font-bold transition-colors ${!filter ? "bg-bleu text-white" : "border border-slate-200 bg-white text-slate-500 hover:border-bleu/40 hover:text-bleu"}`} data-testid="partners-filter-all">
          Tous
        </button>
        {PARTNER_CATEGORIES.map((c) => (
          <button key={c.value} onClick={() => setFilter(c.value)}
            className={`rounded-full px-4 py-2 text-xs font-bold transition-colors ${filter === c.value ? "bg-bleu text-white" : "border border-slate-200 bg-white text-slate-500 hover:border-bleu/40 hover:text-bleu"}`}
            data-testid={`partners-filter-${c.value}`}>
            {c.label}
          </button>
        ))}
      </div>

      {!data ? (
        <div className="flex justify-center py-16"><LoaderCircle className="h-6 w-6 animate-spin text-slate-300" /></div>
      ) : partners.length === 0 ? (
        <div className="card-integra p-10 text-center" data-testid="partners-empty">
          <Handshake className="mx-auto h-10 w-10 text-slate-200" />
          <p className="mt-3 text-sm font-bold text-slate-700">Aucun partenaire {filter ? "dans cette catégorie" : ""} pour le moment</p>
          <p className="mt-1 text-xs text-slate-400">Les partenaires (avocats, associations, centres de formation…) apparaîtront ici dès leur inscription.</p>
        </div>
      ) : (
        <div className="space-y-4">
          {partners.map((p, i) => <PartnerCard key={p.id} p={p} index={i} appliedJobIds={appliedJobIds} onTrack={track} />)}
        </div>
      )}
    </div>
  );
}
